



import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import Lottie from "lottie-react";
import { useRef, useEffect, useState } from "react";
import { fetchHomePage } from "../../services/api";

export default function Web({ lang = "en" }) {
  const [content, setContent] = useState(null);
  const [fallbackContent, setFallbackContent] = useState(null);
  const [webAnimation, setWebAnimation] = useState(null);
  const [webSize, setWebSize] = useState({ width: 0, height: 0 });

  const webRef = useRef(null);
  const blockchainRef = useRef(null);
  const webContainerRef = useRef(null);
  const blockchainContainerRef = useRef(null);
  const lottieSizeRef = useRef(null);

  const [isDesktop, setIsDesktop] = useState(window.innerWidth > 1024);

  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth > 1024);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const updateSize = () => {
      if (lottieSizeRef.current) {
        const width = lottieSizeRef.current.offsetWidth;
        setWebSize({ width, height: Math.round(width * (340 / 680)) });
      }
    };
    updateSize();
    window.addEventListener("resize", updateSize);
    return () => window.removeEventListener("resize", updateSize);
  }, [webAnimation]);

  useEffect(() => {
    fetch("/lottie-files/Web-2.0.json")
      .then((res) => res.json())
      .then((data) => setWebAnimation(data))
      .catch((err) => console.error("Error loading Web 2.0 JSON:", err));
  }, []);

  useEffect(() => {
    setContent(null);
    setFallbackContent(null);
    fetchHomePage(lang)
      .then((res) => {
        if (res?.who_we_are_home?.length) {
          setContent(res);
        } else {
          return fetchHomePage("en").then(setFallbackContent);
        }
      })
      .catch(() => {
        fetchHomePage("en").then(setFallbackContent);
      });
  }, [lang]);

  // ✅ Unified scroll-based IntersectionObserver for ALL screen sizes
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.target === webContainerRef.current) {
            if (entry.isIntersecting) webRef.current?.play();
            else webRef.current?.stop();
          }
          if (entry.target === blockchainContainerRef.current) {
            if (entry.isIntersecting) blockchainRef.current?.play();
            else blockchainRef.current?.stop();
          }
        });
      },
      { threshold: 0.4 }
    );

    if (webContainerRef.current) observer.observe(webContainerRef.current);
    if (blockchainContainerRef.current) observer.observe(blockchainContainerRef.current);

    return () => observer.disconnect();
  }, []); // ✅ No isDesktop dependency — always runs

  const display = content || fallbackContent;

  return (
    <div className="custom-lg-flex">
      {/* Web 2.0 Block — removed onMouseEnter/onMouseLeave */}
      <div
        ref={webContainerRef}
        className="col-lg-6 padding-y-80"
        style={{ backgroundColor: "rgba(188, 226, 245, 1)" }}
      >
        <div className="section-name-wrapper padding-x-40" style={{ alignItems: "center" }}>
          <div className="section-name-dot"></div>
          <h3 className="section-name size-h3 text-dark-blue-color margin-bottom-0">
            {display?.web_heading}
          </h3>
        </div>

        <div ref={lottieSizeRef} className="padding-x-40 margin-top--40" style={{ width: "100%" }}>
          {webAnimation && webSize.width > 0 && (
            <Lottie
              animationData={webAnimation}
              loop={true}
              autoplay={false}
              lottieRef={webRef}
              style={{
                width: isDesktop ? webSize.width : "100%",
                height: isDesktop ? webSize.height : "100%",
              }}
              rendererSettings={{
                preserveAspectRatio: "xMidYMid meet",
                filterSize: { width: "200%", height: "200%", x: "-50%", y: "-50%" },
              }}
            />
          )}
        </div>

        <p className="font-medium text-dark-blue-color h-custom-70 size-h5 padding-x-40 margin-top--40">
          {display?.web_content}
        </p>
      </div>

      {/* Blockchain Block — removed onMouseEnter/onMouseLeave */}
      <div
        ref={blockchainContainerRef}
        className="col-lg-6 padding-y-80"
        style={{ backgroundColor: "rgba(0, 64, 152, 1)" }}
      >
        <div className="section-name-wrapper padding-x-40" style={{ alignItems: "center" }}>
          <div className="section-name-dot"></div>
          <h3 className="section-name size-h3 text-light-blue margin-bottom-0">
            {display?.Blockchain_heading}
          </h3>
        </div>

        <div className="padding-x-40 margin-top--75" style={{ width: "100%", aspectRatio: "680 / 340" }}>
          <DotLottieReact
            src="/lottie-files/Blackchain.lottie"
            loop
            autoplay={false}
            dotLottieRefCallback={(ref) => (blockchainRef.current = ref)}
            style={{ width: "100%", height: "100%" }}
          />
        </div>

        <p className="font-medium padding-top-40 h-custom-70 padding-x-40 size-h5 text-light-blue margin-top--75">
          {display?.Blockchain_content}
        </p>

        <a className="padding-x-40 block-chain-link" target="_blank" href={display?.Blockchain_link_url}>
          {display?.Blockchain_Link_text}
        </a>
      </div>
    </div>
  );
}